import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components/macro';

import { calculateReadyToBuyValuePercent } from '../data/gtm';
import { useTracking } from '../data/gtm';
import Loader, { LoaderWrapper } from '../components/common/Loader';
import {
    useAppContext,
    useAuth,
    useComposition,
    useCompositionTemplates,
    useFrami,
    useImages,
    usePrice,
    useModal,
    useNavigation,
    useOrder,
    usePromotion,
    useProduct,
} from '../context';
import {
    IMAGE_DRAWER,
    ORDER_STATUS,
    TEMPLATE_DRAWER,
    VIDEO_SCREEN,
    VOUCHER,
    PRODUCT_DRAWER,
} from '../providers/NavigationProvider';
import { Api, Storage } from '../utilities';
import { calculateWallWidth, calculateWallHeight, getQueryParams, useNavigateSearch } from '../utilities/helpers';
import { lazyWithPreload } from '../utilities/lazy-with-preload';
import { VOUCHER_FRAMI_ID } from '../data/constants';
import { WorkspaceProvider } from '../providers/WorkspaceProvider';
import { CHECKOUT } from '../providers/NavigationProvider';
import { uiColors } from '../common/styles';
import { logError } from '../utilities/logError';
import CookiesSettings from '../components/studio/CookiesSettings';

const Model3D = lazyWithPreload(() => import(/* webpackChunkName: 'Model3D' */ '../components/studio/Model3D'));

const Composer = lazyWithPreload(() => import(/* webpackChunkName: 'Composer' */ '../components/studio/Composer'));
const InfoPage = lazyWithPreload(() => import(/* webpackChunkName: 'InfoPage' */ '../components/studio/infopage/InfoPage'));

const InfoPageLoaderWrapper = styled.div`
    display: flex;      
    justify-content: center;
    background: white;
    height: 15vh;
    flex-direction: column;
    border-top: 1px solid ${uiColors.DIVIDER};

`;

const StudioWrapper = styled.section`
    display: ${(props) => (props.$hide ? 'none' : 'flex')};
    width: 100vw;
    flex-direction: ${(props) => (props.$isMobile ? 'column' : 'row')};
    // height: 100vh;
    align-items: center;
    background-color: ${(props) => props.$wallColor};
    position: relative;
`;

const OverlaySection = styled.section`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    min-height: ${(props) => (props.$isMobile ? 'none' : ( props.$fullHeight ? '100vh' : '97vh'))};
    z-index: 3001;
    background-color: ${uiColors.GRAY_900};
    opacity: 0.4;
    cursor: auto;
    overflow: hidden;
`;

const Studio = () => {
    const { framiId, compId, drawerId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const {
        height,
        width,
        modelViewer,
        stats,
        orderId,
        compositionTemplateHash,
        drawer,
        couponCode,
        category,
        randomComposition
    } =
        getQueryParams(searchParams);

    const {
        currentFrami,
        isFramiObsolete,
        openLoginModal,
        setOpenLoginModal,
        applyCoupon,
        fetchFrami,
        updateFramiWallShape,
        memoizedWallBackgrounds,
        updateFramiWallBackground,
        setWallBackground,
    } = useFrami();
    const {
        currentComp,
        getOrCreateVoucher,
        setCurrentComp,
        createCompositionFromTemplate,
        fetchComps,
        setFilteredImages,
        setActiveFrame,
    } = useComposition();
    const {
        compositionTemplatesLoaded,
        currentTemplate,
        BASIC_TEMPLATE,
        getCompositionTemplate,
        findRandomTemplateIndex,
        setFilterCategoryId
    } = useCompositionTemplates();
    const { images, fetchImages } = useImages();
    const { loadingPriceModifiers } = usePrice();
    const { promotion, isPromotionActive } = usePromotion();
    const { isProductEditMode } = useProduct();
    const { order, getOrCreateOrder, checkUserOpenOrder } = useOrder();
    const { isMobile } = useAppContext();
    const {
        activeDrawer,
        editMode,
        openDrawer,
        setOpenModelViewer,
        drawerContent,
    } = useNavigation();
    const { user, eventsCounter, setEventsCounter, updateGoogleEventsCounter } = useAuth();
    const [modelUrl, setModelUrl] = useState(null);
    const [modelUrlGltf, setModelUrlGltf] = useState(null);
    const [isExportingGLTF, setIsExportingGLTF] = useState(false);
    const [showInfoPage, setShowInfoPage] = useState(false);

    const [cookies, setCookie] = useCookies(['_seen_video', 'CookieConsent']);
    const { modal, openModal, closeModal } = useModal();
    const { pushEvent } = useTracking();

    const navigate = useNavigateSearch();

    const compositionModelRef = useRef();

    const [showWelcomeScreen, setShowWelcomeScreen] = useState(!cookies._seen_video);
    const [pendingRandomComposition, setPendingRandomComposition] = useState(null);

    const workspaceRef = useRef();
    const workspaceToolsSectionRef = useRef();
    const workspaceMessagesSectionRef = useRef();


    useEffect(() => {
        if (randomComposition) {
            setShowWelcomeScreen(false)
            setCookie('_seen_video', true, { maxAge: 120960000, path: '/' });
        }
    }, [randomComposition]);

    useEffect(() => {
        if (openLoginModal) {
            openModal('login');
            setOpenLoginModal(false);
        }
    }, [openLoginModal]);

    useEffect(() => {
        if (category) {
            setFilterCategoryId(parseInt(category));
            searchParams.delete('category');
            setSearchParams(searchParams);
        }
    }, [category]);

    useEffect(() => {
        // Model3D.preload();
        // CookiesSettings.preload();
        Composer.preload();
        pushEvent('page_view', { page_path: window.location.pathname });
    }, []);

    useEffect(() => {
        if (isFramiObsolete && !currentComp) {
            openModal('loadingCompositions');
        } else if (isFramiObsolete && currentComp && modal.type === 'loadingCompositions') {
            closeModal();
        }
    }, [isFramiObsolete, currentComp, modal]);

    useEffect(() => {
        if (currentFrami && currentComp && currentComp?.id && currentComp?.frami && currentComp?.id !== compId) {
            navigate(`/${currentComp.frami}/${currentComp.id}/`, {
                state: {
                    currentComp: currentComp,
                    drawerId: activeDrawer,
                },
            });
        }
    }, [currentComp?.id]);

    useEffect(() => {
        setOpenModelViewer(!!modelViewer);
    }, [modelViewer]);

    useEffect(() => {
        if (activeDrawer !== VIDEO_SCREEN && currentComp && drawerId) {
            openDrawer(drawerId.toUpperCase());
        }
    }, [drawerId, currentComp?.id]);

    useEffect(() => {
        if ((orderId || (order && order?.status >= 3)) && activeDrawer !== ORDER_STATUS) {
            openDrawer(ORDER_STATUS, true);
        }
    }, [order?.id, orderId]);

    useEffect(() => {
        const init = async () => {
            if (!currentFrami) return;

            if (couponCode) applyCoupon(couponCode);

            const framiIdToUse = framiId ?? currentFrami.id;
            const fetchedImages = await fetchImages(framiIdToUse);
            const imageCount = fetchedImages?.length || 0;

            if (orderId) {
                getOrCreateOrder(orderId, false);
                return;
            }

            if (randomComposition) {
                if (activeDrawer !== TEMPLATE_DRAWER) openDrawer(TEMPLATE_DRAWER);

                let width = 1200;
                let height = 800;
                if (imageCount > 20) {
                    width = 2500;
                    height = 1500;
                } else if (imageCount > 10) {
                    width = 1500;
                    height = 1000;
                }

                const wallShape = [
                    [0, 0],
                    [width, 0],
                    [width, height],
                    [0, height],
                    [0, 0],
                ];
                await updateFramiWallShape(wallShape);

                setPendingRandomComposition({imageCount, width, height, fetchedImages});

                return
            }
            if (compositionTemplateHash) {
                try {
                    const template = await Api.get(`/composer/composition_template/${compositionTemplateHash}/`);
                    const x = width ?? template.width;
                    const y = height ?? template.height;
                    const wallShape = [
                        [0, 0],
                        [x, 0],
                        [x, y],
                        [0, y],
                        [0, 0],
                    ];
                    await updateFramiWallShape(wallShape);
                    const composition = await createCompositionFromTemplate(template, fetchedImages);
                    setCurrentComp(composition);
                } catch (err) {
                    logError(err);
                }
            } else {
                const compositionCount = await fetchComps(framiId ?? currentFrami.id, compId);

                if (drawer === VOUCHER) {
                    getOrCreateVoucher();
                } else if (compositionCount === 0) {
                    const composition = await createCompositionFromTemplate(BASIC_TEMPLATE);
                    setCurrentComp(composition);
                }
            }

            const intervalId = setInterval(() => fetchImages(framiIdToUse), 55 * 60 * 1000);
            return () => clearInterval(intervalId);
        }

        init();
    }, [currentFrami?.id]);

    useEffect(() => {
        const createRandomComposition = async () => {
            if (!pendingRandomComposition || !compositionTemplatesLoaded) return;

            const { imageCount, width, height, fetchedImages } = pendingRandomComposition;

            try {
                const templateIndex = await findRandomTemplateIndex(Math.max(imageCount, 3), { width, height });
                if (templateIndex) {
                    const template = await getCompositionTemplate(templateIndex);
                    if (template) {
                        const composition = await createCompositionFromTemplate(template, fetchedImages);
                        setCurrentComp(composition);

                        searchParams.delete('random_composition');
                        setSearchParams(searchParams);
                    }
                }
            } catch (err) {
                logError(err);
            } finally {
                setPendingRandomComposition(false);
            }
        };

        createRandomComposition();
    }, [pendingRandomComposition, compositionTemplatesLoaded]);

    useEffect(() => {
        if (!currentComp || !currentComp.frames_json || !memoizedWallBackgrounds || memoizedWallBackgrounds.size === 0 || isProductEditMode)
            return;
        const defaultWallBackground = memoizedWallBackgrounds.get(currentFrami?.default_wall_background);
        const wallWidth = calculateWallWidth(currentFrami?.wall_shape);
        const wallHeight = calculateWallHeight(currentFrami?.wall_shape);
        if (
            defaultWallBackground &&
            defaultWallBackground.max_width_cm >= currentComp.width / 10 &&
            defaultWallBackground.max_height_cm >= currentComp.height / 10 &&
            defaultWallBackground.max_width_cm >= wallWidth / 10 &&
            defaultWallBackground.max_height_cm >= wallHeight / 10
        ) {
            setWallBackground(defaultWallBackground)
            return;
        }
        let wallBackground;
        if (!defaultWallBackground) {
            wallBackground = [...memoizedWallBackgrounds.values()].filter(
                (wallBackground) =>
                    wallBackground.max_width_cm > currentComp.width / 10 &&
                    wallBackground.max_height_cm > currentComp.height / 10 &&
                    wallBackground.max_width_cm >= wallWidth / 10 &&
                    wallBackground.max_height_cm >= wallHeight / 10
            )[0];
        }
        if (wallBackground) {
            updateFramiWallBackground(wallBackground.id);
        } else {
            setWallBackground(null);
        }
    }, [currentComp, currentFrami?.default_wall_background, memoizedWallBackgrounds, isProductEditMode]);

    useEffect(() => {
        if (currentFrami && !orderId && !loadingPriceModifiers) {
            checkUserOpenOrder();
        }
    }, [currentFrami?.id, loadingPriceModifiers]);

    useEffect(() => {
        if (order?.coupon && currentFrami?.coupon?.code !== order?.coupon?.code) {
            applyCoupon(order.coupon.code);
        }
    }, [currentFrami?.coupon, order?.coupon]);

    useEffect(() => {
        if (currentFrami && !currentFrami?.coupon && promotion?.coupon && isPromotionActive && !order) {
            applyCoupon(promotion.coupon.code)
        }
    }, [currentFrami, promotion, isPromotionActive, order]);

    useEffect(() => {
        if (currentComp && currentComp.is_voucher && !currentComp.is_ordered) {
            if (activeDrawer !== VOUCHER) {
                setActiveFrame(currentComp.frames_json[0]);
                openDrawer(VOUCHER, true);
            }
        } else if (
            currentComp &&
            currentComp.is_ordered &&
            currentComp.order_id &&
            currentComp.order_id !== parseInt(order?.id)
        ) {
            getOrCreateOrder(currentComp.order_id, false);
            if (activeDrawer !== ORDER_STATUS) {
                openDrawer(ORDER_STATUS, true);
            }
        } else if (
            currentComp &&
            ![TEMPLATE_DRAWER, PRODUCT_DRAWER].includes(activeDrawer) &&
            !editMode &&
            !currentComp.is_ordered
        ) {
            openDrawer(TEMPLATE_DRAWER);
        }
        if (currentComp && currentFrami?.id !== currentComp?.frami) {
            fetchFrami(currentComp?.frami);
        }
    }, [currentComp?.id]);

    useEffect(() => {
        if (currentComp && currentComp.baseline_data && activeDrawer === TEMPLATE_DRAWER) {
            openDrawer(IMAGE_DRAWER, true);
        }
    }, [activeDrawer, currentComp]);

    useEffect(() => {
        const onSelectTemplate = async () => {
            const composition = await createCompositionFromTemplate(currentTemplate);
            setCurrentComp(composition);
        };

        if (currentTemplate) {
            onSelectTemplate();
        }
    }, [currentTemplate]);

    useEffect(() => {
        const readyToBuy = localStorage.getItem('ready_to_buy');
        const updateReadyToBuy = async () => {
            await Api.post('/auth/update_ready_to_buy/');
        };
        if (
            currentComp &&
            !currentComp.is_ordered &&
            !isNaN(currentComp.price?.rounded?.total) &&
            !isEmpty(currentComp.frames_json) &&
            currentComp.frames_json?.length > 1 &&
            currentComp.frames_json.every((f) => f.image && f.calculated?.dpi >= 100) &&
            images?.filter((i) => !i.is_voucher && i.frami_id !== VOUCHER_FRAMI_ID).length <=
                currentComp.frames_json?.length
        ) {
            const updatedEventsCount = (eventsCounter['ready_to_buy'] || 0) + 1;
            setEventsCounter((prev) => ({ ...prev, ready_to_buy: updatedEventsCount }));
            const valuePercent = calculateReadyToBuyValuePercent(updatedEventsCount);

            const eventParameters = {
                price: currentComp.price?.rounded?.total,
                // value: 0.02 * currentComp.price?.rounded?.total, // Przyjęta waga konwersji na podstawie danych z Ads vs przychód
                value: valuePercent * currentComp.price?.rounded?.total,
                discounted: currentComp.price?.rounded?.discounted,
                price_in_pln: currentComp.price_in_pln,
                currency: currentFrami?.currency,
                frameCount: currentComp.frames_json.length,
                width: currentComp.width,
                height: currentComp.height,
            };
            updateGoogleEventsCounter({ ...eventsCounter, ready_to_buy: updatedEventsCount });
            if (!readyToBuy) {
                pushEvent('first_ready_to_buy', eventParameters);
                updateReadyToBuy();
                Storage.setItem('ready_to_buy', true);
            }
            pushEvent('ready_to_buy', eventParameters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [images.length]);


    useEffect(() => {
        const exportGLTF = async () => {
          // Dynamiczny import modułu GLTFExporter
          const { GLTFExporter } = await import('three/addons/exporters/GLTFExporter');
      
          const exporterGLB = new GLTFExporter();
          const exporterGLTF = new GLTFExporter();
      
          if (compositionModelRef.current) {
            exporterGLB.parse(
              compositionModelRef.current,
              (result) => {
                const blob = new Blob([result], { type: 'application/octet-stream' });
                const url = URL.createObjectURL(blob);
                setModelUrl(url);
              },
              (error) => {
                console.log('An error happened during parsing', error);
              },
              { binary: true }
            );
      
            exporterGLTF.parse(
              compositionModelRef.current,
              (result) => {
                const blob = new Blob([JSON.stringify(result)], { type: 'text/plain' });
                const url = URL.createObjectURL(blob);
                setModelUrlGltf(url);
              }
            );
          }
        };
      
        if (isExportingGLTF) {
          setTimeout(exportGLTF, 200);
        }
      }, [isExportingGLTF]);

    const handleCloseModelViewer = () => {
        setModelUrl(null);
        setFilteredImages(null);
        setIsExportingGLTF(false);
        setOpenModelViewer(false);
    };

    useEffect(() => {
        if (showWelcomeScreen && !randomComposition) {
            openModal('welcomeScreen', { setShowWelcomeScreen: setShowWelcomeScreen })
        }
    }, [showWelcomeScreen, randomComposition]);

    // preload InfoPage after 5 seconds
    useEffect(() => {
        const timer = setTimeout(() => {
            InfoPage.preload();
        }, 5000);
        return () => clearTimeout(timer);
    }, []);
    // on first scroll switch Loader to InfoPage
    useEffect(() => {
        const handleScroll = () => {
            setShowInfoPage(true);
            window.removeEventListener('scroll', handleScroll);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);


    if (!currentFrami) {
        return (
            <LoaderWrapper $overlap={true}>
                <Loader />
            </LoaderWrapper>
        );
    }

    return (
        <>

            <WorkspaceProvider
                workspaceRef={workspaceRef}
                workspaceToolsSectionRef={workspaceToolsSectionRef}
                workspaceMessagesSectionRef={workspaceMessagesSectionRef}
                setIsExportingGLTF={setIsExportingGLTF}
                isExportingGLTF={isExportingGLTF}
            >
                {drawerContent && activeDrawer !== CHECKOUT && (
                    <OverlaySection />
                )}
                <StudioWrapper
                    $isMobile={isMobile}
                    id="studio-wrapper"
                    $wallColor={currentFrami.wall_color}
                    $fullHeight={activeDrawer === CHECKOUT}
                >
                    <CookiesSettings />
                    {modelUrl ? (
                        <Model3D modelUrl={modelUrl} modelUrlGltf={modelUrlGltf} onClose={handleCloseModelViewer} />
                    ) : (
                        <Composer
                            compositionModelRef={compositionModelRef}
                            stats={stats}
                            workspaceRef={workspaceRef}
                            workspaceToolsSectionRef={workspaceToolsSectionRef}
                            workspaceMessagesSectionRef={workspaceMessagesSectionRef}
                        />
                    )}
                </StudioWrapper>
                {/* Render Loader until user scrolls, then show InfoPage */}
                { showInfoPage ? <InfoPage /> : <InfoPageLoaderWrapper><Loader /> </InfoPageLoaderWrapper> }
            </WorkspaceProvider>
        </>
    );
};

export default Studio;
